import React from 'react';
import { SectionType, PageSection, IndustryType, SiteData, SitePage, YachtItem, BlogPost } from '../../types';
import {
  Menu,
  Sparkles,
  BarChart3,
  FileText,
  Briefcase,
  Building2,
  MessageSquareQuote,
  Users,
  Image,
  HelpCircle,
  CreditCard,
  BookOpen,
  Ship,
  Filter,
  Calendar,
  Mail,
  Layers,
  LayoutTemplate,
  Compass,
  Anchor,
} from 'lucide-react';

import { HeaderSection } from './sections/HeaderSection';
import { HeroSection } from './sections/HeroSection';
import { StatsSection } from './sections/StatsSection';
import { AboutSection } from './sections/AboutSection';
import { ServicesSection } from './sections/ServicesSection';
import { ReferencesSection } from './sections/ReferencesSection';
import { TestimonialsSection } from './sections/TestimonialsSection';
import { TeamSection } from './sections/TeamSection';
import { GallerySection } from './sections/GallerySection';
import { FaqSection } from './sections/FaqSection';
import { PricingSection } from './sections/PricingSection';
import { BlogPreviewSection } from './sections/BlogPreviewSection';
import { YachtFleetSection } from './sections/YachtFleetSection';
import { YachtFilterSection } from './sections/YachtFilterSection';
import { ReservationCtaSection } from './sections/ReservationCtaSection';
import { YachtRoutesSection } from './sections/YachtRoutesSection';
import { YachtMarinasSection } from './sections/YachtMarinasSection';
import { YachtAvailabilityCalendarSection } from './sections/YachtAvailabilityCalendarSection';
import { ContactSection } from './sections/ContactSection';
import { CustomContentSection } from './sections/CustomContentSection';
import { FooterSection } from './sections/FooterSection';

export interface SectionDescriptor {
  type: SectionType;
  name: string;
  category: 'NAVIGATION' | 'HERO' | 'CONTENT' | 'SOCIAL_PROOF' | 'YACHT' | 'MEDIA' | 'CONTACT';
  description: string;
  icon: any;
  allowedIndustries: IndustryType[];
  defaultContent: Record<string, any>;
  defaultStyle: Record<string, any>;
}

export interface SectionRendererProps {
  section: PageSection;
  siteData: SiteData;
  activePage: SitePage;
  isPreview?: boolean;
  onNavigate?: (slug: string) => void;
  onSelectYacht?: (yacht: YachtItem) => void;
  onSelectPost?: (post: BlogPost) => void;
  onOpenHeaderSettings?: () => void;
  onOpenImagePicker?: () => void;
  onAddService?: () => void;
}

export const COMPONENT_REGISTRY: Record<SectionType, SectionDescriptor> = {
  HEADER: {
    type: 'HEADER',
    name: 'Üst Menü (Header)',
    category: 'NAVIGATION',
    description: 'Logo, site sayfaları navigasyonu ve sağ tarafta iletişim butonu.',
    icon: Menu,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      title: 'Adiosoft Brand',
      subtitle: 'Kurumsal Çözümler',
      buttonText: 'İletişime Geçin',
      buttonLink: '/iletisim',
    },
    defaultStyle: {
      backgroundColor: '#0F172A',
      textColor: '#FFFFFF',
    },
  },
  HERO: {
    type: 'HERO',
    name: 'Hero Karşılama Alanı',
    category: 'HERO',
    description: 'Büyük görsel arkaplan, dikkat çekici manşet ve aksiyon butonları.',
    icon: Sparkles,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'YENİ NESİL ÇÖZÜMLER',
      title: 'İşletmenizi Geleceğe Taşıyan Güçlü Dijital Varlık',
      description: 'Profesyonel kurumsal altyapı ve yenilikçi hizmet modelleri ile daima bir adım öndesiniz.',
      buttonText: 'Hizmetlerimizi İnceleyin',
      buttonLink: '/hizmetler',
      secondaryButtonText: 'Hakkımızda',
      secondaryButtonLink: '/hakkimizda',
      bgImageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop',
      bgOverlayOpacity: 70,
    },
    defaultStyle: {
      textColor: '#FFFFFF',
      paddingTop: '100px',
      paddingBottom: '100px',
    },
  },
  STATS: {
    type: 'STATS',
    name: 'Metrikler & Sayılar',
    category: 'CONTENT',
    description: '4 sütunlu başarı rakamları ve istatistik vitrini.',
    icon: BarChart3,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'Rakamlarla Biz',
      title: 'Başarı Göstergelerimiz',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
    },
  },
  ABOUT: {
    type: 'ABOUT',
    name: 'Hakkımızda & Tanıtım',
    category: 'CONTENT',
    description: 'Sol taraf görsel veya metin, sağ taraf kurumsal tarihçe ve vizyon.',
    icon: FileText,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'KURUMSAL KİMLİK',
      title: 'Değer Üreten, Güven İnşa Eden Global Birikim',
      description: 'Uzun yıllara dayanan tecrübemiz, etik değerlerimiz ve müşteri odaklı vizyonumuzla sektörde öncüyüz.',
      imageUrl: '',
      buttonText: 'Detaylı Bilgi',
      buttonLink: '/hakkimizda',
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  SERVICES: {
    type: 'SERVICES',
    name: 'Hizmetler & Uzmanlıklar',
    category: 'CONTENT',
    description: 'İkonlu ve açıklamalı 4 sütunlu hizmet kartları.',
    icon: Briefcase,
    allowedIndustries: ['CORPORATE'],
    defaultContent: {
      badge: 'UZMANLIK ALANLARI',
      title: 'İşletmenizi Zirveye Taşıyan Bütünsel Çözümler',
      description: 'Sektörün ihtiyaç duyduğu modern stratejik ve operasyonel çözümler.',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
    },
  },
  REFERENCES: {
    type: 'REFERENCES',
    name: 'Referanslar & Markalar',
    category: 'SOCIAL_PROOF',
    description: 'Müşteri, iş ortağı veya holding logoları ızgarası.',
    icon: Building2,
    allowedIndustries: ['CORPORATE'],
    defaultContent: {
      badge: 'GÜVENİLİR ORTAKLIKLAR',
      title: 'Sektör Liderlerinin Tercihi',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  TESTIMONIALS: {
    type: 'TESTIMONIALS',
    name: 'Müşteri Yorumları & Değerlendirmeler',
    category: 'SOCIAL_PROOF',
    description: 'Yıldızlı müşteri referansları, alıntılar ve yönetici isimleri.',
    icon: MessageSquareQuote,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'MÜŞTERİ MEMNUNİYETİ',
      title: 'Müşterilerimizin Gözünden Deneyimler',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
    },
  },
  TEAM: {
    type: 'TEAM',
    name: 'Yönetim & Ekip',
    category: 'CONTENT',
    description: 'Fotoğraflı ekip üyeleri, unvanlar ve sosyal profil bağlantıları.',
    icon: Users,
    allowedIndustries: ['CORPORATE'],
    defaultContent: {
      badge: 'LİDERLİK KADROSU',
      title: 'Deneyimli ve Vizyoner Ekibimiz',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  GALLERY: {
    type: 'GALLERY',
    name: 'Fotoğraf Galerisi',
    category: 'MEDIA',
    description: 'Görsel portföy, ofis, filo veya proje fotoğrafları ızgarası.',
    icon: Image,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'FOTOĞRAF GALERİSİ',
      title: 'Seçkin Görseller ve Kareler',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  FAQ: {
    type: 'FAQ',
    name: 'Sıkça Sorulan Sorular (SSS)',
    category: 'CONTENT',
    description: 'Açılır kapanır akordeon soru ve cevap listesi.',
    icon: HelpCircle,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'SIKÇA SORULAN SORULAR',
      title: 'Merak Edilen Konular',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
    },
  },
  PRICING: {
    type: 'PRICING',
    name: 'Fiyatlandırma Paketleri',
    category: 'CONTENT',
    description: '3 sütunlu hizmet fiyat paketleri ve özellik listeleri.',
    icon: CreditCard,
    allowedIndustries: ['CORPORATE'],
    defaultContent: {
      badge: 'FİYATLANDIRMA',
      title: 'İhtiyacınıza Uygun Esnek Planlar',
      items: [],
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  BLOG_PREVIEW: {
    type: 'BLOG_PREVIEW',
    name: 'Blog & Haber Vitrini',
    category: 'MEDIA',
    description: 'Sitenin son makaleleri, bültenleri ve sektör haberleri kartları.',
    icon: BookOpen,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'GÜNCEL YAZILAR',
      title: 'Haberler ve Sektörel Analizler',
      description: 'En son gelişmeler, uzman makaleleri ve sektörel rehberler.',
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  YACHT_FLEET: {
    type: 'YACHT_FLEET',
    name: 'Kiralık Yat & Gulet Filosu',
    category: 'YACHT',
    description: 'Tekne fotoğrafları, metre, kabin, kişi kapasitesi ve günlük fiyat vitrini.',
    icon: Ship,
    allowedIndustries: ['YACHT_RENTAL'],
    defaultContent: {
      badge: 'LÜKS FİLO',
      title: 'Kiralık Motoryat, Gulet ve Süperyat Filomuz',
      description: 'Ege ve Akdeniz koylarında unutulmaz mavi yolculuk deneyimi sunan seçkin teknelerimiz.',
    },
    defaultStyle: {
      backgroundColor: '#F8FAFC',
      textColor: '#032030',
    },
  },
  YACHT_FILTER: {
    type: 'YACHT_FILTER',
    name: 'Yat Arama & Filtreleme Barı',
    category: 'YACHT',
    description: 'Liman, tekne tipi ve kişi sayısına göre interaktif arama motoru kutusu.',
    icon: Filter,
    allowedIndustries: ['YACHT_RENTAL'],
    defaultContent: {
      title: 'Hayalinizdeki Seyri Planlayın',
      subtitle: 'Kişi sayısı, kabin tercihi ve bütçenize göre ideal yatı bulun',
    },
    defaultStyle: {
      backgroundColor: '#003554',
      textColor: '#FFFFFF',
    },
  },
  RESERVATION_CTA: {
    type: 'RESERVATION_CTA',
    name: 'Rezervasyon Talep Çağrısı',
    category: 'YACHT',
    description: 'Müsaitlik sorgulama, erken rezervasyon avantajları ve talep formu butonu.',
    icon: Calendar,
    allowedIndustries: ['YACHT_RENTAL'],
    defaultContent: {
      badge: 'ERKEN REZERVASYON FIRSATLARI',
      title: 'Bu Yaz Unutulmaz Bir Mavi Yolculuğa Hazır mısınız?',
      description: 'İstediğiniz tarihler için müsaitlik sorgulayın, özel indirim paketlerimizden yararlanın.',
      buttonText: 'Rezervasyon Formunu Doldurun',
      buttonLink: '/rezervasyon',
    },
    defaultStyle: {
      backgroundColor: '#032030',
      textColor: '#FFFFFF',
    },
  },
  YACHT_ROUTES: {
    type: 'YACHT_ROUTES',
    name: 'Mavi Tur Rotaları & Destinasyonlar',
    category: 'YACHT',
    description: 'Gün süresi, koylar, başlangıç ve varış limanlarını içeren popüler mavi yolculuk rotaları.',
    icon: Compass,
    allowedIndustries: ['YACHT_RENTAL'],
    defaultContent: {
      badge: 'POPÜLER SEYİR ROTALARI',
      title: 'Ege ve Akdeniz’in Büyüleyici Mavi Yolculuk Rotaları',
      description: 'Turkuaz koylardan antik limanlara, özenle planlanmış haftalık seyir programlarımızla rüya gibi bir tatil yaşayın.',
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  YACHT_MARINAS: {
    type: 'YACHT_MARINAS',
    name: 'Bağlama Limanları & Marinalar',
    category: 'YACHT',
    description: 'Liman koordinatları, maksimum LOA/derinlik ve VIP marina hizmetleri listesi.',
    icon: Anchor,
    allowedIndustries: ['YACHT_RENTAL'],
    defaultContent: {
      badge: 'BAĞLAMA LİMANLARI & MARİNALAR',
      title: 'Lüks Filonuz İçin Ana Çıkış ve Bağlama Noktaları',
      description: 'Uluslararası standartlarda modern hizmetler sunan marinalardan güvenli ve konforlu şekilde seyre başlayın.',
    },
    defaultStyle: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
    },
  },
  AVAILABILITY_CALENDAR: {
    type: 'AVAILABILITY_CALENDAR',
    name: 'Yat Müsaitlik & Doluluk Takvimi',
    category: 'YACHT',
    description: 'Seçilen teknenin güncel doluluk ve boş günlerini gösteren interaktif takvim.',
    icon: Calendar,
    allowedIndustries: ['YACHT_RENTAL'],
    defaultContent: {
      badge: 'CANLI MÜSAİTLİK DURUMU',
      title: 'Müsaitlik ve Doluluk Takvimi',
      description: 'Filomuzdaki teknelerin güncel doluluk takvimini inceleyin, uygun tarih aralığı için anında rezervasyon talebinizi iletin.',
    },
    defaultStyle: {
      backgroundColor: '#0F172A',
      textColor: '#FFFFFF',
    },
  },
  CONTACT: {
    type: 'CONTACT',
    name: 'İletişim & Talep Formu',
    category: 'CONTACT',
    description: 'İletişim bilgileri, WhatsApp, harita ve mesaj gönderme formu.',
    icon: Mail,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      badge: 'BİZE ULAŞIN',
      title: 'Bizimle İletişime Geçin',
      description: 'Her türlü soru ve teklif talepleriniz için uzman ekibimizle görüşün.',
      buttonText: 'Mesaj Gönder',
    },
    defaultStyle: {
      backgroundColor: '#0F172A',
      textColor: '#FFFFFF',
    },
  },
  CUSTOM_CONTENT: {
    type: 'CUSTOM_CONTENT',
    name: 'Özel Zengin İçerik Alanı',
    category: 'CONTENT',
    description: 'Dilediğiniz özel HTML, duyuru metni veya ek açıklamalar.',
    icon: LayoutTemplate,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      title: 'Özel Başlık',
      description: 'Buraya dilediğiniz açıklamayı yazabilirsiniz.',
    },
    defaultStyle: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
    },
  },
  FOOTER: {
    type: 'FOOTER',
    name: 'Alt Bilgi (Footer)',
    category: 'NAVIGATION',
    description: 'Telif hakkı, hızlı linkler, adres bilgileri ve yukarı çık butonu.',
    icon: Layers,
    allowedIndustries: ['CORPORATE', 'YACHT_RENTAL'],
    defaultContent: {
      title: 'Adiosoft Enterprise',
      description: 'Tüm hakları saklıdır. Güvenilir dijital altyapı.',
    },
    defaultStyle: {
      backgroundColor: '#020617',
      textColor: '#94A3B8',
    },
  },
};

export const SECTION_RENDERERS: Record<SectionType, React.FC<SectionRendererProps>> = {
  HEADER: HeaderSection,
  HERO: HeroSection,
  STATS: StatsSection,
  ABOUT: AboutSection,
  SERVICES: ServicesSection,
  REFERENCES: ReferencesSection,
  TESTIMONIALS: TestimonialsSection,
  TEAM: TeamSection,
  GALLERY: GallerySection,
  FAQ: FaqSection,
  PRICING: PricingSection,
  BLOG_PREVIEW: BlogPreviewSection,
  YACHT_FLEET: YachtFleetSection,
  YACHT_FILTER: YachtFilterSection,
  RESERVATION_CTA: ReservationCtaSection,
  YACHT_ROUTES: YachtRoutesSection,
  YACHT_MARINAS: YachtMarinasSection,
  AVAILABILITY_CALENDAR: YachtAvailabilityCalendarSection,
  CONTACT: ContactSection,
  CUSTOM_CONTENT: CustomContentSection,
  FOOTER: FooterSection,
};

export const getAvailableSectionsForIndustry = (industry: IndustryType): SectionDescriptor[] => {
  return Object.values(COMPONENT_REGISTRY).filter((descriptor) =>
    descriptor.allowedIndustries.includes(industry)
  );
};
